import React, { useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/Layout/DashboardLayout';
import {
  MagnifyingGlassIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  QuestionMarkCircleIcon,
  TagIcon,
  SparklesIcon,
  LightBulbIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

const KeywordResearch = () => {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [showReport, setShowReport] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const fetchHistory = async (kw, start, end) => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (kw) params.append('keyword', kw);
      if (start) params.append('startDate', start);
      if (end) params.append('endDate', end);
      params.append('limit', '200');
      const response = await fetch(`/api/keywords/history?${params.toString()}`);
      const data = await response.json();
      setHistory(data.history || []);
      setShowHistory(true);
    } catch (err) {
      console.error('History fetch error:', err);
      setHistory([]);
    }
    setHistoryLoading(false);
  };

  const loadHistoryRecord = async (id) => {
    try {
      const response = await fetch(`/api/keywords/history/${id}/detail`);
      const data = await response.json();
      if (data.record?.fullData) {
        setResults(data.record.fullData);
        setKeyword(data.record.keyword);
      }
    } catch (err) {
      console.error('Record load error:', err);
    }
  };
  const reportRef = useRef(null);

  const suggestions = ['SEO tools', 'content marketing', 'link building', 'keyword research', 'on-page SEO'];

  const handleSearch = async () => {
    if (!keyword.trim()) {
      toast.error('Please enter a keyword');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/keywords/research', { keyword });
      setResults(response.data);
      toast.success('Keyword research completed!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch keyword data');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    if (difficulty <= 30) return 'text-green-600 bg-green-100';
    if (difficulty <= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getDifficultyText = (difficulty) => {
    if (difficulty <= 30) return 'Easy';
    if (difficulty <= 60) return 'Medium';
    return 'Hard';
  };

  const getAiScore = (data) => {
    if (!data) return 0;
    let score = 50;
    if (data.searchVolume > 1000) score += 15;
    else if (data.searchVolume > 100) score += 10;
    else if (data.searchVolume > 0) score += 5;
    if (data.difficulty <= 30) score += 20;
    else if (data.difficulty <= 60) score += 10;
    if (data.cpc > 1) score += 10;
    if (data.trend?.length > 0) score += 5;
    return Math.min(score, 100);
  };

  const exportToCSV = () => {
    if (!results) return;
    const rows = [['Keyword', 'Search Volume', 'CPC', 'Difficulty', 'Intent']];
    rows.push([results.keyword, results.searchVolume, results.cpc, results.difficulty, results.intent || 'N/A']);
    if (results.relatedKeywords?.length) {
      rows.push([], ['Related Keywords']);
      rows.push(['Keyword', 'Search Volume', 'CPC', 'Difficulty', 'Intent']);
      results.relatedKeywords.forEach(kw => {
        rows.push([kw.keyword, kw.searchVolume, kw.cpc, kw.difficulty, kw.intent || 'N/A']);
      });
    }
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keyword-research-${keyword.replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported to CSV!');
  };

  const exportToPDF = async () => {
    if (!reportRef.current) return;
    setExportingPdf(true);
    try {
      const reportEl = reportRef.current;
      const canvas = await html2canvas(reportEl, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        ignoreElements: (node) => !!node.shadowRoot && !reportEl.contains(node)
      });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 10;
      const pageW = pdf.internal.pageSize.getWidth() - margin * 2;
      const pageH = pdf.internal.pageSize.getHeight() - margin * 2;
      const pxPerMm = canvas.width / pageW;
      const sliceH = Math.floor(pageH * pxPerMm);

      for (let y = 0, page = 0; y < canvas.height; y += sliceH, page++) {
        const h = Math.min(sliceH, canvas.height - y);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = h;
        slice.getContext('2d').drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
        if (page > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', margin, margin, pageW, h / pxPerMm);
      }

      pdf.save(`keyword-report-${keyword.replace(/\s+/g, '-')}.pdf`);
      toast.success('PDF report downloaded!');
    } catch (error) {
      toast.error('Failed to generate PDF');
      console.error(error);
    } finally {
      setExportingPdf(false);
    }
  };

  const getTrendData = () => {
    if (!results?.trend?.length) return [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const ordered = [...results.trend].sort((a, b) =>
      ((a.year || 0) - (b.year || 0)) || ((a.month || 0) - (b.month || 0))
    );
    return ordered.map((item, index) => ({
      month: item.month ? `${months[item.month - 1] || 'M' + item.month} ${item.year || ''}`.trim() : `Month ${index + 1}`,
      volume: item.search_volume || item.count || item || 0
    }));
  };

  const getDifficultyChartData = () => {
    if (!results) return [];
    const d = results.difficulty || 0;
    return [
      { name: 'Difficulty', value: d },
      { name: 'Remaining', value: 100 - d }
    ];
  };

  const getCompetitorChartData = () => {
    if (!results?.competitors?.length) return [];
    return results.competitors.slice(0, 5).map(comp => ({
      name: comp.domain?.length > 15 ? comp.domain.substring(0, 15) + '...' : comp.domain,
      authority: comp.authority || 0,
      backlinks: comp.backlinks || 0,
      position: comp.position || 0
    }));
  };

  const getRelatedKeywordsChartData = () => {
    if (!results?.relatedKeywords?.length) return [];
    return results.relatedKeywords.slice(0, 8).map(kw => ({
      keyword: kw.keyword?.length > 20 ? kw.keyword.substring(0, 20) + '...' : kw.keyword,
      volume: kw.searchVolume || 0,
      difficulty: kw.difficulty || 0
    }));
  };

  const getScoreRadarData = () => {
    if (!results) return [];
    const maxVol = 10000;
    return [
      { metric: 'Volume', value: Math.min((results.searchVolume / maxVol) * 100, 100), fullMark: 100 },
      { metric: 'CPC Value', value: Math.min(results.cpc * 20, 100), fullMark: 100 },
      { metric: 'Low Difficulty', value: 100 - (results.difficulty || 0), fullMark: 100 },
      { metric: 'AI Score', value: getAiScore(results), fullMark: 100 },
      { metric: 'Opportunity', value: results.difficulty <= 30 && results.searchVolume > 100 ? 90 : results.difficulty <= 60 ? 60 : 30, fullMark: 100 },
      { metric: 'Competition', value: Math.min(((results.competitors?.length || 0) / 5) * 100, 100), fullMark: 100 }
    ];
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'related', name: 'Related Keywords', icon: TagIcon },
    { id: 'questions', name: 'Questions', icon: QuestionMarkCircleIcon },
    { id: 'longtail', name: 'Long-tail', icon: ArrowTrendingUpIcon },
    { id: 'competitors', name: 'Competitors', icon: DocumentDuplicateIcon }
  ];


  const KEYWORD_FAQ = [
    { q: "What is keyword research and why is it essential for SEO?", a: "Keyword research is the process of discovering and analyzing search terms that people enter into search engines. It is the foundation of any successful SEO strategy because it reveals what your target audience is searching for, the volume of those searches, and how competitive each keyword is. Effective keyword research helps you create content that aligns with user intent, driving qualified organic traffic to your website." },
    { q: "How does SERPfinity keyword research differ from other tools?", a: "SERPfinity combines traditional keyword metrics like search volume, CPC, and competition with AI-powered insights including AEO opportunity scores, question-based keyword clustering, and AI search citation potential. Our tool identifies not just what people search in Google, but what questions AI assistants are answering, helping you optimize for both traditional and AI-powered search simultaneously." },
    { q: "What metrics should I look for when evaluating keywords?", a: "Key metrics include monthly search volume (how many people search the term), keyword difficulty (how hard it is to rank), CPC (commercial value indicator), search intent (informational, navigational, transactional, or commercial), SERP features present, and click-through rate potential. SERPfinity also provides AI visibility scores showing how likely the keyword triggers AI-generated answers." },
    { q: "How do I find long-tail keywords for my niche?", a: "Long-tail keywords are longer, more specific phrases with lower competition but higher conversion rates. SERPfinity discovers them through question mining from People Also Ask, Related Searches analysis, autocomplete suggestions, competitor gap analysis, and AI search query patterns. Our tool clusters related long-tail keywords by topic, making it easy to plan comprehensive content that captures multiple search queries." },
    { q: "What is keyword intent and how does it affect content strategy?", a: "Keyword intent describes the purpose behind a search query. The four main types are informational (seeking knowledge), navigational (finding a specific site), transactional (ready to buy), and commercial investigation (comparing options). Matching your content format and depth to keyword intent is critical. SERPfinity automatically classifies intent for each keyword and recommends optimal content types." },
    { q: "How often should I update my keyword research?", a: "Keyword landscapes shift continuously due to seasonal trends, emerging topics, algorithm updates, and evolving AI search behaviors. We recommend refreshing core keyword research quarterly and monitoring high-priority keywords weekly. SERPfinity provides automated alerts when significant keyword trends emerge, search volumes shift dramatically, or new AI search opportunities appear in your niche." }
  ];

  return (
    <>
      <Helmet>
        <title>Keyword Research Tool | Find SEO & AEO Keywords | SERPfinity</title>
        <meta name="description" content="Discover high-value keywords for SEO and AI search optimization. Analyze search volume, competition, intent, and AEO opportunities with SERPfinity keyword research tool." />
        <meta name="keywords" content="keyword research tool, SEO keyword finder, long-tail keywords, keyword difficulty, search volume analyzer, keyword intent, AEO keywords, AI search keywords, SERPfinity" />
        <link rel="canonical" href="https://thedigitalhall.club/keyword-research" />
        <meta property="og:title" content="Keyword Research Tool | SERPfinity" />
        <meta property="og:description" content="Find profitable keywords for SEO and AI search. Analyze volume, difficulty, intent, and optimization opportunities." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thedigitalhall.club/keyword-research" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Keyword Research Tool | SERPfinity" />
        <meta name="twitter:description" content="Discover high-value SEO and AEO keywords. Analyze search volume, competition, and AI optimization opportunities." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":KEYWORD_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
      </Helmet>
      <DashboardLayout
      title="Keyword Research"
      subtitle="Discover high-value keywords with search volume, competition, and AI insights"
    >
      <div className="max-w-7xl mx-auto">

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter a keyword to research..."
                autoComplete="off"
                name="kwsearch_nofill"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2 transition duration-200"
            >
              {loading ? (
                <><svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> Researching...</>
              ) : (
                <>Research Keyword</>
              )}
            </button>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-sm text-blue-600 font-medium">Popular searches:</span>
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  setKeyword(suggestion);
                  handleSearch();
                }}
                className="text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full transition duration-200"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>


        {/* Historical Data & Date Range */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              Research History
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                onClick={() => fetchHistory(keyword, startDate, endDate)}
                disabled={historyLoading}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition disabled:opacity-50"
              >
                {historyLoading ? 'Loading...' : 'Search History'}
              </button>
              {showHistory && (
                <button
                  onClick={() => setShowHistory(false)}
                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition"
                >
                  Hide
                </button>
              )}
            </div>
          </div>

          {showHistory && (
            <div className="overflow-x-auto">
              {history.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Keyword</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Volume</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">CPC</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Difficulty</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Intent</th>
                      <th className="text-center py-2 px-3 font-medium text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-blue-50 transition">
                        <td className="py-2 px-3 text-gray-500">{new Date(item.searchedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                        <td className="py-2 px-3 font-medium text-gray-800">{item.keyword}</td>
                        <td className="py-2 px-3 text-right">{item.searchVolume?.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right">${item.cpc?.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${item.difficulty > 70 ? 'bg-red-100 text-red-700' : item.difficulty > 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                            {item.difficulty}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded text-xs ${item.intent === 'commercial' ? 'bg-purple-100 text-purple-700' : item.intent === 'transactional' ? 'bg-green-100 text-green-700' : item.intent === 'navigational' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                            {item.intent}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <button
                            onClick={() => loadHistoryRecord(item.id)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium hover:underline"
                          >
                            View Report
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No history records found. Research some keywords to start building your history.</p>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        {results && (
          <>
            {/* Main Keyword Overview with Export Buttons */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex flex-wrap justify-between items-start mb-4 gap-2">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{results.keyword}</h2>
                  <p className="text-gray-500">Primary keyword analysis</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowReport(!showReport)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 text-sm font-medium"
                  >
                    <ChartBarIcon className="h-4 w-4" />
                    {showReport ? 'Hide Report' : 'View Report'}
                  </button>
                  <button
                    onClick={exportToPDF}
                    disabled={exportingPdf || !showReport}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition duration-200 text-sm font-medium"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    {exportingPdf ? 'Generating...' : 'Download PDF'}
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 text-sm font-medium"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    Export CSV
                  </button>
                </div>
              </div>

              {/* Metric Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 rounded-lg border-l-4 border-blue-500 bg-blue-50">
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-medium text-blue-600">Search Volume</p>
                    <ChartBarIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{results.searchVolume?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Monthly searches</p>
                </div>
                <div className="p-4 rounded-lg border-l-4 border-green-500 bg-green-50">
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-medium text-green-600">CPC</p>
                    <CurrencyDollarIcon className="h-5 w-5 text-green-400" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-1">${results.cpc?.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">Cost per click</p>
                </div>
                <div className="p-4 rounded-lg border-l-4 border-purple-500 bg-purple-50">
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-medium text-purple-600">Difficulty</p>
                    <ArrowTrendingUpIcon className="h-5 w-5 text-purple-400" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{results.difficulty}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getDifficultyColor(results.difficulty)}`}>
                    {getDifficultyText(results.difficulty)}
                  </span>
                </div>
                <div className="p-4 rounded-lg border-l-4 border-orange-500 bg-orange-50">
                  <div className="flex justify-between items-start">
                    <p className="text-sm font-medium text-orange-600">AI Score</p>
                    <SparklesIcon className="h-5 w-5 text-orange-400" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{getAiScore(results)}/100</p>
                  <p className="text-xs text-gray-500">AI visibility potential</p>
                </div>
              </div>

              {/* Search Trend Chart */}
              {getTrendData().length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Search Trend (12 months)</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={getTrendData()}>
                      <defs>
                        <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip />
                      <Area type="monotone" dataKey="volume" stroke="#3B82F6" strokeWidth={2} fill="url(#trendGradient)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Visual Report Dashboard */}
            {showReport && (
              <div ref={reportRef} className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="border-b pb-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <ChartBarIcon className="h-6 w-6 text-indigo-600" />
                        Keyword Research Report
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">Comprehensive analysis for "{results.keyword}"  Generated {new Date().toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">SERPfinity</p>
                      <p className="text-xs text-gray-400">AI-Powered SEO Platform</p>
                    </div>
                  </div>
                </div>

                {/* Report Metric Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                    <p className="text-3xl font-bold text-blue-700">{results.searchVolume?.toLocaleString()}</p>
                    <p className="text-xs text-blue-600 font-medium mt-1">Search Volume</p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                    <p className="text-3xl font-bold text-green-700">${results.cpc?.toFixed(2)}</p>
                    <p className="text-xs text-green-600 font-medium mt-1">CPC</p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                    <p className="text-3xl font-bold text-purple-700">{results.difficulty}</p>
                    <p className="text-xs text-purple-600 font-medium mt-1">Difficulty ({getDifficultyText(results.difficulty)})</p>
                  </div>
                  <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl">
                    <p className="text-3xl font-bold text-orange-700">{getAiScore(results)}/100</p>
                    <p className="text-xs text-orange-600 font-medium mt-1">AI Score</p>
                  </div>
                </div>

                {/* Charts Row 1 - Trend & Difficulty */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  {/* Search Trend Line Chart */}
                  <div className="md:col-span-2 border rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">Search Volume Trend</h3>
                    {getTrendData().length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={getTrendData()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <RechartsTooltip />
                          <Line type="monotone" dataKey="volume" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">No trend data available</div>
                    )}
                  </div>

                  {/* Difficulty Donut */}
                  <div className="border rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">Keyword Difficulty</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={getDifficultyChartData()}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={80}
                          dataKey="value"
                          startAngle={90}
                          endAngle={-270}
                        >
                          <Cell fill={results.difficulty <= 30 ? '#10B981' : results.difficulty <= 60 ? '#F59E0B' : '#EF4444'} />
                          <Cell fill="#E5E7EB" />
                        </Pie>
                        <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" className="text-2xl font-bold" fill="#1F2937">
                          {results.difficulty}
                        </text>
                        <text x="50%" y="62%" textAnchor="middle" dominantBaseline="middle" className="text-xs" fill="#6B7280">
                          {getDifficultyText(results.difficulty)}
                        </text>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Charts Row 2 - Related Keywords & Radar */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Related Keywords Bar Chart */}
                  <div className="border rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">Related Keywords by Volume</h3>
                    {getRelatedKeywordsChartData().length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={getRelatedKeywordsChartData()} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 10 }} />
                          <YAxis dataKey="keyword" type="category" width={120} tick={{ fontSize: 9 }} />
                          <RechartsTooltip />
                          <Bar dataKey="volume" fill="#3B82F6" radius={[0, 4, 4, 0]}>
                            {getRelatedKeywordsChartData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-gray-400 text-sm">No related keywords data</div>
                    )}
                  </div>

                  {/* Keyword Opportunity Radar */}
                  <div className="border rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">Keyword Opportunity Score</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <RadarChart data={getScoreRadarData()}>
                        <PolarGrid stroke="#E5E7EB" />
                        <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 8 }} />
                        <Radar name="Score" dataKey="value" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} strokeWidth={2} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Charts Row 3 - Competitors */}
                {getCompetitorChartData().length > 0 && (
                  <div className="border rounded-xl p-4 mb-8">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">Top Competitors - Domain Authority</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={getCompetitorChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="authority" name="Domain Authority" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="position" name="SERP Position" fill="#10B981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Keyword Insights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <LightBulbIcon className="h-5 w-5 text-blue-600" />
                      <h4 className="font-semibold text-blue-800">SEO Opportunity</h4>
                    </div>
                    <p className="text-sm text-blue-700">
                      {results.difficulty <= 30
                        ? 'Great opportunity! Low competition with good search volume.'
                        : results.difficulty <= 60
                        ? 'Moderate opportunity. Consider long-tail variations for better results.'
                        : 'High competition keyword. Focus on niche variations and content depth.'}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <div className="flex items-center gap-2 mb-2">
                      <SparklesIcon className="h-5 w-5 text-purple-600" />
                      <h4 className="font-semibold text-purple-800">AI Strategy</h4>
                    </div>
                    <p className="text-sm text-purple-700">
                      Optimize for featured snippets and FAQ schema to improve AI visibility. Target an AI score above 80 for best results.
                    </p>
                  </div>
                </div>

                {/* SERP Features */}
                {results.serp?.serpFeatures && Object.keys(results.serp.serpFeatures).length > 0 && (
                  <div className="border rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3">SERP Features Present</h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(results.serp.serpFeatures).map(([feature, present]) => (
                        present && (
                          <span key={feature} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/* Related Keywords Table in Report */}
                {results.relatedKeywords?.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <TagIcon className="h-4 w-4 text-indigo-500" />
                      Top Related Keywords
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Keyword</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Volume</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">CPC</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Difficulty</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Intent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.relatedKeywords.slice(0, 15).map((kw, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-3 font-medium">{kw.keyword}</td>
                              <td className="py-2 px-3 text-right">{kw.searchVolume?.toLocaleString()}</td>
                              <td className="py-2 px-3 text-right">${kw.cpc?.toFixed(2)}</td>
                              <td className="py-2 px-3 text-right">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  kw.difficulty <= 30 ? 'bg-green-100 text-green-700' :
                                  kw.difficulty <= 60 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {kw.difficulty}
                                </span>
                              </td>
                              <td className="py-2 px-3">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  kw.intent === 'informational' ? 'bg-blue-100 text-blue-700' :
                                  kw.intent === 'commercial' ? 'bg-purple-100 text-purple-700' :
                                  kw.intent === 'transactional' ? 'bg-green-100 text-green-700' :
                                  kw.intent === 'navigational' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {kw.intent || 'N/A'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Question Keywords in Report */}
                {results.relatedKeywords?.filter(kw =>
                  kw.keyword?.toLowerCase().startsWith('how') ||
                  kw.keyword?.toLowerCase().startsWith('what') ||
                  kw.keyword?.toLowerCase().startsWith('why') ||
                  kw.keyword?.toLowerCase().startsWith('when') ||
                  kw.keyword?.toLowerCase().startsWith('where') ||
                  kw.keyword?.toLowerCase().startsWith('who') ||
                  kw.keyword?.toLowerCase().startsWith('can') ||
                  kw.keyword?.toLowerCase().startsWith('does') ||
                  kw.keyword?.toLowerCase().startsWith('is')
                ).length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <QuestionMarkCircleIcon className="h-4 w-4 text-blue-500" />
                      Question Keywords
                    </h3>
                    <div className="space-y-2">
                      {results.relatedKeywords.filter(kw =>
                        kw.keyword?.toLowerCase().startsWith('how') ||
                        kw.keyword?.toLowerCase().startsWith('what') ||
                        kw.keyword?.toLowerCase().startsWith('why') ||
                        kw.keyword?.toLowerCase().startsWith('when') ||
                        kw.keyword?.toLowerCase().startsWith('where') ||
                        kw.keyword?.toLowerCase().startsWith('who') ||
                        kw.keyword?.toLowerCase().startsWith('can') ||
                        kw.keyword?.toLowerCase().startsWith('does') ||
                        kw.keyword?.toLowerCase().startsWith('is')
                      ).slice(0, 10).map((kw, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="flex items-center gap-2">
                            <QuestionMarkCircleIcon className="h-4 w-4 text-blue-400" />
                            <span className="font-medium text-sm">{kw.keyword}</span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span>Vol: {kw.searchVolume?.toLocaleString()}</span>
                            <span>CPC: ${kw.cpc?.toFixed(2)}</span>
                            <span className={`px-2 py-0.5 rounded-full font-medium ${
                              kw.difficulty <= 30 ? 'bg-green-100 text-green-700' :
                              kw.difficulty <= 60 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              Diff: {kw.difficulty}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Long-tail Keywords in Report */}
                {results.relatedKeywords?.some(kw => kw.keyword?.split(' ').length >= 3) && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                      Long-tail Keywords
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Keyword</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Volume</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">CPC</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Difficulty</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Intent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.relatedKeywords.filter(kw => kw.keyword?.split(' ').length >= 3).slice(0, 15).map((kw, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-3 font-medium">{kw.keyword}</td>
                              <td className="py-2 px-3 text-right">{kw.searchVolume?.toLocaleString()}</td>
                              <td className="py-2 px-3 text-right">${kw.cpc?.toFixed(2)}</td>
                              <td className="py-2 px-3 text-right">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  kw.difficulty <= 30 ? 'bg-green-100 text-green-700' :
                                  kw.difficulty <= 60 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {kw.difficulty}
                                </span>
                              </td>
                              <td className="py-2 px-3">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                  kw.intent === 'informational' ? 'bg-blue-100 text-blue-700' :
                                  kw.intent === 'commercial' ? 'bg-purple-100 text-purple-700' :
                                  kw.intent === 'transactional' ? 'bg-green-100 text-green-700' :
                                  kw.intent === 'navigational' ? 'bg-orange-100 text-orange-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {kw.intent || 'N/A'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Competitors in Report */}
                {results.competitors?.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <DocumentDuplicateIcon className="h-4 w-4 text-red-500" />
                      SERP Competitors
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left py-2 px-3 font-medium text-gray-600">#</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">Domain</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Position</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">DA</th>
                            <th className="text-right py-2 px-3 font-medium text-gray-600">Backlinks</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-600">URL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.competitors.map((comp, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-3 font-medium text-gray-400">{index + 1}</td>
                              <td className="py-2 px-3 font-medium">{comp.domain}</td>
                              <td className="py-2 px-3 text-right">{comp.position}</td>
                              <td className="py-2 px-3 text-right">{comp.authority}</td>
                              <td className="py-2 px-3 text-right">{comp.backlinks?.toLocaleString()}</td>
                              <td className="py-2 px-3 text-blue-500 truncate max-w-xs">{comp.url}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="border-b">
                <nav className="flex -mb-px">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setSelectedTab(tab.id)}
                      className={`py-3 px-6 flex items-center space-x-2 border-b-2 font-medium text-sm transition-colors ${
                        selectedTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {selectedTab === 'overview' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Keyword Insights</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <LightBulbIcon className="h-5 w-5 text-blue-600" />
                          <h4 className="font-semibold text-blue-800">SEO Opportunity</h4>
                        </div>
                        <p className="text-sm text-blue-700">
                          {results.difficulty <= 30
                            ? 'Great opportunity! Low competition with good search volume.'
                            : results.difficulty <= 60
                            ? 'Moderate opportunity. Consider long-tail variations for better results.'
                            : 'High competition keyword. Focus on niche variations and content depth.'}
                        </p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                        <div className="flex items-center gap-2 mb-2">
                          <SparklesIcon className="h-5 w-5 text-purple-600" />
                          <h4 className="font-semibold text-purple-800">AI Strategy</h4>
                        </div>
                        <p className="text-sm text-purple-700">
                          Optimize for featured snippets and FAQ schema to improve AI visibility.
                        </p>
                      </div>
                    </div>

                    <h3 className="text-lg font-semibold mb-4">Top Related Keywords</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Keyword</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">Volume</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">CPC</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">Difficulty</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Intent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.relatedKeywords?.slice(0, 10).map((kw, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">{kw.keyword}</td>
                              <td className="py-3 px-4 text-right">{kw.searchVolume?.toLocaleString()}</td>
                              <td className="py-3 px-4 text-right">${kw.cpc?.toFixed(2)}</td>
                              <td className="py-3 px-4 text-right">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(kw.difficulty)}`}>
                                  {kw.difficulty}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">{kw.intent || 'N/A'}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Related Keywords Tab */}
                {selectedTab === 'related' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Related Keywords</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Keyword</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">Volume</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">CPC</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">Difficulty</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Intent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.relatedKeywords?.map((kw, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">{kw.keyword}</td>
                              <td className="py-3 px-4 text-right">{kw.searchVolume?.toLocaleString()}</td>
                              <td className="py-3 px-4 text-right">${kw.cpc?.toFixed(2)}</td>
                              <td className="py-3 px-4 text-right">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(kw.difficulty)}`}>
                                  {kw.difficulty}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">{kw.intent || 'N/A'}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Questions Tab */}
                {selectedTab === 'questions' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Question Keywords</h3>
                    <div className="space-y-3">
                      {results.relatedKeywords?.filter(kw =>
                        kw.keyword?.toLowerCase().startsWith('how') ||
                        kw.keyword?.toLowerCase().startsWith('what') ||
                        kw.keyword?.toLowerCase().startsWith('why') ||
                        kw.keyword?.toLowerCase().startsWith('when') ||
                        kw.keyword?.toLowerCase().startsWith('where') ||
                        kw.keyword?.toLowerCase().startsWith('who') ||
                        kw.keyword?.toLowerCase().startsWith('can') ||
                        kw.keyword?.toLowerCase().startsWith('does') ||
                        kw.keyword?.toLowerCase().startsWith('is')
                      ).map((kw, index) => (
                        <div key={index} className="p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <QuestionMarkCircleIcon className="h-5 w-5 text-blue-500" />
                              <span className="font-medium">{kw.keyword}</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>Vol: {kw.searchVolume?.toLocaleString()}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(kw.difficulty)}`}>
                                {kw.difficulty}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!results.relatedKeywords?.some(kw =>
                        ['how','what','why','when','where','who','can','does','is'].some(q => kw.keyword?.toLowerCase().startsWith(q))
                      )) && (
                        <p className="text-gray-500 text-center py-8">No question-based keywords found for this term.</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Long-tail Tab */}
                {selectedTab === 'longtail' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Long-tail Keywords</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="text-left py-3 px-4 font-medium text-gray-600">Keyword</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">Volume</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">CPC</th>
                            <th className="text-right py-3 px-4 font-medium text-gray-600">Difficulty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.relatedKeywords?.filter(kw => kw.keyword?.split(' ').length >= 3).map((kw, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">{kw.keyword}</td>
                              <td className="py-3 px-4 text-right">{kw.searchVolume?.toLocaleString()}</td>
                              <td className="py-3 px-4 text-right">${kw.cpc?.toFixed(2)}</td>
                              <td className="py-3 px-4 text-right">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(kw.difficulty)}`}>
                                  {kw.difficulty}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {(!results.relatedKeywords?.some(kw => kw.keyword?.split(' ').length >= 3)) && (
                        <p className="text-gray-500 text-center py-8">No long-tail keywords found.</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Competitors */}
                {selectedTab === 'competitors' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Top Ranking Competitors</h3>
                    <div className="space-y-3">
                      {results.competitors?.map((comp, index) => (
                        <div key={index} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <span className="text-lg font-semibold text-gray-900">#{index + 1}</span>
                              <div>
                                <p className="font-medium text-gray-900">{comp.domain}</p>
                                <p className="text-sm text-gray-500">{comp.title}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-500">Position: {comp.position}</span>
                              <span className="text-sm text-gray-500">DA: {comp.authority}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>Backlinks: {comp.backlinks?.toLocaleString()}</span>
                            {comp.url && (
                              <a href={comp.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline truncate max-w-md block">
                                {comp.url}
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                      {(!results.competitors || results.competitors.length === 0) && (
                        <p className="text-gray-500 text-center py-8">No competitor data available.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div></>
        )}

        {/* Empty State */}
        {!results && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <MagnifyingGlassIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Start Your Keyword Research</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Enter a keyword above to discover search volume, competition, related keywords, and AI optimization opportunities.
            </p>
          </div>
        )}
      </div>
      <FAQSection items={KEYWORD_FAQ} />
    </DashboardLayout>
    </>
  );
};

export default KeywordResearch;
